import type { Bilingual, MarketingLocale } from '../lib/marketingLocale'

/**
 * Head and headline for the two legal documents, /terms and /privacy.
 *
 * The BODY of these pages stays as JSX in `pages/legal/`. A contract is a text
 * with numbered cross-references, nested subsections and inline links, not a
 * content model; extracting it into typed records would buy nothing and would
 * make the numbering easy to break. What lives here is only the part that two
 * places have to agree on: the H1 that the OG card prints, the "as of" date and
 * the title and description `useSeo` writes. `MARKETING_CATALOGUE` reads `h1`
 * from here rather than restating it, so the card and the page cannot drift
 * apart — the same rule the press, subprocessors and founding entries follow.
 *
 * **Bilingual since 2026-09-09, reversing the earlier "English only,
 * deliberately" decision.** The old note argued that translating a contract
 * produces a SECOND binding document and that one language was the honest state
 * until somebody owned the drift. Three things moved:
 *
 *  1. **English-only is not the cautious option for these customers.** German
 *     courts read §305(2) BGB to mean standard terms are only incorporated into
 *     a contract if the counterparty had a reasonable opportunity to take notice
 *     of them, and long English-only terms put in front of a German-speaking
 *     consumer fail that test — the Berlin Court of Appeals held exactly this
 *     against WhatsApp. When terms are not validly incorporated the statutory
 *     defaults fill the gap, so what is lost is the LIABILITY LIMITATIONS, not
 *     merely the translation. StudioFlow sells to German-speaking Swiss studios.
 *  2. **The second-binding-document risk is handled by a prevailing-language
 *     clause** — the industry answer (Google's cloud terms among them):
 *     translations are a convenience and the English version governs on
 *     conflict. That turns drift from a legal problem into a maintenance
 *     problem, and a maintenance problem is something a build guard can hold.
 *     `__tests__/legalTranslationParity.test.tsx` is that guard: it fails when
 *     the two versions can have silently diverged.
 *  3. **The privacy policy is a different animal from the terms.** It is a
 *     NOTICE, not a contract, so there is no second-binding-text risk at all,
 *     and the EDPB-endorsed transparency guidelines (WP260 rev.01) say a
 *     translation should be provided where the controller targets speakers of
 *     that language. Swiss practice agrees: no statutory language list, but the
 *     notice has to be in a language the data subjects actually read.
 *
 * So both documents are now PAIRS of URLs on the same mechanism every other
 * marketing document uses — `MARKETING_DOCUMENT_PATHS`, `Bilingual<T>` and
 * `hreflangPair()` — which also means `MarketingLanguageSwitch` renders on them.
 *
 * ⚠ This is the MARKETING bilingual system (en/de), not the app's six-locale
 * i18n contract. Legal bodies do NOT go into `src/i18n/locales/`, and there are
 * deliberately no fr/it/es/pt versions: every extra language is another
 * indexable document carrying legal claims somebody has to own.
 *
 * The English version governs in both documents; the German is a convenience
 * translation, which is exactly what the prevailing-language clause makes safe.
 * `__tests__/legalTranslationParity.test.tsx` holds the two in step so that
 * "someone willing to own it" is a build failure rather than a promise.
 */
export interface LegalPageCopy {
  /** The page's own H1, and therefore the headline printed on its OG card. */
  h1: string
  /**
   * Machine-readable "as of" date (ISO). The parity guard compares these, so a
   * German version left behind an edited English one fails the build.
   */
  updatedOn: string
  /** Rendered under the H1 — derived from `updatedOn`, never typed by hand. */
  updated: string
  meta: { title: string; description: string }
}

const MONTHS: Bilingual<readonly string[]> = {
  en: ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'],
  de: ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
    'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'],
}

const UPDATED_PREFIX: Bilingual<string> = {
  en: 'Last updated',
  de: 'Letzte Aktualisierung',
}

/**
 * "Last updated: September 2026" from an ISO date.
 *
 * Derived rather than typed so the visible line and the date the parity guard
 * compares cannot disagree — a hand-written display string is exactly where a
 * German version silently keeps last spring's date after an English edit.
 * `Intl` is deliberately not used: the month has to be identical in a test, in
 * a prerendered page and on a build agent with any ICU data.
 */
function updatedLine(locale: MarketingLocale, iso: string): string {
  const [year, month] = iso.split('-')
  return `${UPDATED_PREFIX[locale]}: ${MONTHS[locale][Number(month) - 1]} ${year}`
}

function legal(
  locale: MarketingLocale,
  updatedOn: string,
  copy: Omit<LegalPageCopy, 'updated' | 'updatedOn'>,
): LegalPageCopy {
  return { ...copy, updatedOn, updated: updatedLine(locale, updatedOn) }
}

/**
 * The anchor sentence of the prevailing-language clause, in each language.
 *
 * Exported because the parity guard asserts it is present in BOTH versions:
 * without it the old objection stands and the German text is a second binding
 * document rather than a convenience translation.
 */
export const PREVAILING_LANGUAGE_ANCHOR: Bilingual<string> = {
  en: 'the English version prevails',
  de: 'ist die englische Fassung massgebend',
}

/** aria-label of the notice the German versions carry above section 1. */
export const PREVAILING_LANGUAGE_NOTICE_LABEL = 'Sprachfassung'

export const TERMS: Bilingual<LegalPageCopy> = {
  en: legal('en', '2026-09-09', {
    h1: 'Terms & Conditions',
    meta: {
      title: 'Terms & Conditions — StudioFlow',
      description:
        'The terms covering StudioFlow accounts, subscriptions and billing, fair use and storage, data ownership, suspension and liability. Governed by Swiss law, Zurich jurisdiction.',
    },
  }),
  de: legal('de', '2026-09-09', {
    h1: 'Allgemeine Geschäftsbedingungen',
    meta: {
      title: 'Allgemeine Geschäftsbedingungen — StudioFlow',
      description:
        'Die Bedingungen für StudioFlow-Konten, Abonnemente und Rechnungsstellung, Fair Use und Speicherplatz, Dateneigentum, Sperrung und Haftung. Es gilt Schweizer Recht, Gerichtsstand Zürich.',
    },
  }),
}

export const PRIVACY: Bilingual<LegalPageCopy> = {
  en: legal('en', '2026-09-09', {
    h1: 'Privacy Policy',
    meta: {
      title: 'Privacy Policy — StudioFlow',
      description:
        'What StudioFlow collects, why, where it is stored and how long it is kept — with your rights under the GDPR and the Swiss FADP (nDSG) and how to exercise them.',
    },
  }),
  de: legal('de', '2026-09-09', {
    h1: 'Datenschutzerklärung',
    meta: {
      title: 'Datenschutzerklärung — StudioFlow',
      description:
        'Was StudioFlow erhebt, warum, wo die Daten gespeichert werden und wie lange sie aufbewahrt bleiben — mit Ihren Rechten nach DSGVO und Schweizer DSG (nDSG) und wie Sie diese ausüben.',
    },
  }),
}

/** Both documents, keyed the same way `MARKETING_DOCUMENT_PATHS` keys them. */
export const LEGAL_DOCUMENTS = { terms: TERMS, privacy: PRIVACY } as const

export type LegalDocumentKey = keyof typeof LEGAL_DOCUMENTS
